/**
 * VixoKart Theme — Core JavaScript
 * Handles: scroll reveal, header behavior, accordions, mobile nav,
 * predictive search, toast notifications, global utilities
 */

'use strict';

// ─── Utility Functions ────────────────────────────────────────────────────────

// Preserve utilities created by section-level scripts that can load before
// this global asset (for example, collection-filters.js).
const VixoKart = window.VixoKart = window.VixoKart || {};

/**
 * Format money using Shopify's money format.
 * @param {number} cents - Amount in cents
 * @returns {string} Formatted money string
 */
VixoKart.formatMoney = function(cents) {
  const amount = (cents / 100).toFixed(2);
  const format = window.Shopify.moneyFormat || '${{amount}}';
  return format.replace('{{amount}}', amount)
               .replace('{{amount_no_decimals}}', Math.round(cents / 100))
               .replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
};

/**
 * Show a toast notification.
 * @param {string} message
 * @param {'default'|'success'|'error'} type
 * @param {number} duration - ms
 */
VixoKart.toast = function(message, type = 'default', duration = 3000) {
  const container = document.getElementById('toast-container');
  if (!container) return;

  const toast = document.createElement('div');
  toast.className = `toast${type !== 'default' ? ` toast--${type}` : ''}`;
  toast.textContent = message;
  toast.setAttribute('role', 'alert');
  container.appendChild(toast);

  setTimeout(() => {
    toast.classList.add('out');
    toast.addEventListener('animationend', () => toast.remove(), { once: true });
  }, duration);
};

/**
 * Debounce a function.
 */
VixoKart.debounce = function(fn, delay = 300) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), delay);
  };
};

/**
 * Fetch JSON from a URL.
 */
VixoKart.fetchJSON = async function(url, options = {}) {
  const response = await fetch(url, {
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    ...options,
  });
  if (!response.ok) throw new Error(`HTTP error ${response.status}`);
  return response.json();
};

// ─── Scroll Reveal ─────────────────────────────────────────────────────────────

(function initScrollReveal() {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -48px 0px' });

  document.querySelectorAll('.reveal').forEach(el => observer.observe(el));

  // Safety net: if anything is somehow never detected as visible (edge cases
  // with certain browsers/extensions), force it visible after 1.5s so content
  // can never get stuck invisible.
  setTimeout(() => {
    document.querySelectorAll('.reveal:not(.visible)').forEach(el => el.classList.add('visible'));
  }, 1500);

  // Re-run for dynamically added elements
  VixoKart.observeReveal = function(el) { observer.observe(el); };
})();

// ─── Header ─────────────────────────────────────────────────────────────────────

(function initHeader() {
  const header = document.querySelector('.site-header');
  if (!header) return;

  const SCROLL_THRESHOLD = 40;
  let lastScroll = 0;

  function updateHeader() {
    const scroll = window.scrollY;
    if (scroll > SCROLL_THRESHOLD) {
      header.classList.add('scrolled');
    } else {
      header.classList.remove('scrolled');
    }
    lastScroll = scroll;
  }

  // Initial call
  updateHeader();

  window.addEventListener('scroll', VixoKart.debounce(updateHeader, 10), { passive: true });
})();

// ─── Mobile Navigation ──────────────────────────────────────────────────────────

(function initMobileNav() {
  const hamburger = document.querySelector('.site-header__hamburger');
  const mobileNav = document.querySelector('.mobile-nav');
  const overlay   = document.querySelector('.mobile-nav__overlay');
  const closeBtn  = document.querySelector('.mobile-nav__close');

  if (!hamburger || !mobileNav) return;

  function openNav() {
    mobileNav.classList.add('active');
    document.body.style.overflow = 'hidden';
    hamburger.setAttribute('aria-expanded', 'true');
  }

  function closeNav() {
    mobileNav.classList.remove('active');
    document.body.style.overflow = '';
    hamburger.setAttribute('aria-expanded', 'false');
  }

  hamburger.addEventListener('click', openNav);
  overlay?.addEventListener('click', closeNav);
  closeBtn?.addEventListener('click', closeNav);

  // Sub-menus
  document.querySelectorAll('.mobile-nav__link[data-toggle]').forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      const target = document.getElementById(link.dataset.toggle);
      if (target) {
        target.classList.toggle('active');
        link.querySelector('.mobile-nav__chevron')?.classList.toggle('rotate');
      }
    });
  });

  // ESC key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && mobileNav.classList.contains('active')) closeNav();
  });
})();

// ─── Accordion ──────────────────────────────────────────────────────────────────

(function initAccordions() {
  document.querySelectorAll('.accordion__trigger').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const item    = trigger.closest('.accordion__item');
      const content = item.querySelector('.accordion__content');
      const body    = item.querySelector('.accordion__body');
      const isOpen  = item.classList.contains('open');

      // Close all siblings
      trigger.closest('.accordion')
        ?.querySelectorAll('.accordion__item.open')
        .forEach(openItem => {
          openItem.classList.remove('open');
          const c = openItem.querySelector('.accordion__content');
          if (c) c.style.height = '0';
          openItem.querySelector('.accordion__trigger')?.setAttribute('aria-expanded', 'false');
        });

      if (!isOpen) {
        item.classList.add('open');
        if (content && body) {
          content.style.height = body.offsetHeight + 'px';
        }
        trigger.setAttribute('aria-expanded', 'true');
      }
    });

    trigger.setAttribute('aria-expanded', 'false');
  });
})();

// ─── FAQ Accordion ──────────────────────────────────────────────────────────────

(function initFAQ() {
  document.querySelectorAll('.faq-question').forEach(question => {
    question.addEventListener('click', () => {
      const item   = question.closest('.faq-item');
      const answer = item.querySelector('.faq-answer');
      const inner  = item.querySelector('.faq-answer__inner');
      const isOpen = item.classList.contains('open');

      document.querySelectorAll('.faq-item.open').forEach(openItem => {
        openItem.classList.remove('open');
        const a = openItem.querySelector('.faq-answer');
        if (a) a.style.height = '0';
        openItem.querySelector('.faq-question')?.setAttribute('aria-expanded', 'false');
      });

      if (!isOpen) {
        item.classList.add('open');
        if (answer && inner) answer.style.height = inner.offsetHeight + 'px';
        question.setAttribute('aria-expanded', 'true');
      }
    });

    question.setAttribute('aria-expanded', 'false');
  });
})();

// ─── Predictive Search ──────────────────────────────────────────────────────────

(function initPredictiveSearch() {
  const forms = document.querySelectorAll('.header-search__form[data-predictive]');

  forms.forEach(form => {
    const input   = form.querySelector('.header-search__input');
    const results = form.querySelector('.predictive-search');
    if (!input || !results) return;

    const search = VixoKart.debounce(async (query) => {
      if (query.length < 2) {
        results.classList.remove('active');
        results.innerHTML = '';
        return;
      }

      try {
        const url = `${window.routes.predictive_search_url}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5&section_id=predictive-search`;
        const response = await fetch(url);
        const text = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const content = doc.querySelector('#shopify-section-predictive-search');
        if (content) {
          results.innerHTML = content.innerHTML;
          results.classList.add('active');
        }
      } catch (err) {
        console.error('Predictive search error:', err);
      }
    }, 300);

    input.addEventListener('input', (e) => search(e.target.value.trim()));

    input.addEventListener('focus', () => {
      if (input.value.length >= 2) results.classList.add('active');
    });

    document.addEventListener('click', (e) => {
      if (!form.contains(e.target)) {
        results.classList.remove('active');
      }
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        results.classList.remove('active');
        input.blur();
      }
    });
  });
})();

// ─── Animated Search Placeholder ────────────────────────────────────────────────

(function initAnimatedPlaceholder() {
  const TERMS = [
    'Power Tools',
    'Cordless Drill',
    'Safety Gloves',
    'Cleaning Supplies',
    'Hand Tools',
    'Angle Grinder',
    'Work Boots',
    'Construction Tools',
    'Measuring Tape',
    'Tool Bags',
  ];

  const PREFIX   = 'Search for "';
  const SUFFIX   = '"';
  const TYPE_SPD = 60;   // ms per character typed
  const DEL_SPD  = 30;   // ms per character deleted
  const PAUSE    = 1800; // ms to hold the complete word
  const BETWEEN  = 400;  // ms gap between words

  document.querySelectorAll('[data-animated-placeholder]').forEach(input => {
    // Skip if the input already has a user value
    if (input.value.trim()) return;

    let termIndex  = 0;
    let charIndex  = 0;
    let isDeleting = false;
    let timer      = null;
    let paused     = false; // stop animating while user is focused

    function tick() {
      const term    = TERMS[termIndex];
      input.setAttribute('placeholder', isDeleting
        ? PREFIX + term.slice(0, charIndex) + SUFFIX
        : PREFIX + term.slice(0, charIndex) + SUFFIX);

      if (!isDeleting) {
        charIndex++;
        if (charIndex > term.length) {
          // Fully typed — pause then start deleting
          isDeleting = true;
          timer = setTimeout(tick, PAUSE);
          return;
        }
      } else {
        charIndex--;
        if (charIndex < 0) {
          // Fully deleted — move to next term
          isDeleting = false;
          charIndex  = 0;
          termIndex  = (termIndex + 1) % TERMS.length;
          timer = setTimeout(tick, BETWEEN);
          return;
        }
      }

      timer = setTimeout(tick, isDeleting ? DEL_SPD : TYPE_SPD);
    }

    function start() {
      paused = false;
      if (!input.value.trim()) tick();
    }

    function stop() {
      paused = true;
      clearTimeout(timer);
      // Restore a clean placeholder while focused
      input.setAttribute('placeholder', 'Search products…');
    }

    input.addEventListener('focus', stop);
    input.addEventListener('blur',  () => {
      if (!input.value.trim()) start();
    });

    // Kick off
    timer = setTimeout(tick, 800);
  });
})();

// ─── Newsletter Form ─────────────────────────────────────────────────────────────

(function initNewsletterForms() {
  document.querySelectorAll('.newsletter-form, .footer-newsletter__form-el').forEach(form => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const input = form.querySelector('input[type="email"]');
      const btn   = form.querySelector('button[type="submit"]');
      if (!input || !btn) return;

      const email = input.value.trim();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        VixoKart.toast(window.cartStrings?.error || 'Please enter a valid email.', 'error');
        return;
      }

      btn.classList.add('btn--loading');
      btn.disabled = true;

      try {
        const body = new FormData();
        body.append('contact[email]', email);
        body.append('form_type', 'customer');
        body.append('utf8', '✓');

        const response = await fetch('/contact#contact_form', { method: 'POST', body });
        if (response.ok || response.status === 200) {
          VixoKart.toast('Thank you for subscribing!', 'success');
          input.value = '';
        }
      } catch {
        VixoKart.toast('Something went wrong. Please try again.', 'error');
      } finally {
        btn.classList.remove('btn--loading');
        btn.disabled = false;
      }
    });
  });
})();

// ─── Generic Modal System (size guide, etc.) ─────────────────────────────────────

(function initModals() {
  function openModal(modal) {
    modal.classList.add('active');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    modal.querySelector('.modal__close')?.focus();
  }

  function closeModal(modal) {
    modal.classList.remove('active');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open-modal]');
    if (opener) {
      const modal = document.getElementById(opener.dataset.openModal);
      if (modal) openModal(modal);
      return;
    }
    const closer = e.target.closest('[data-modal-close]');
    if (closer) {
      const modal = closer.closest('[data-modal]');
      if (modal) closeModal(modal);
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('[data-modal].active').forEach(closeModal);
  });
})();

// ─── Wishlist (persisted in localStorage) ────────────────────────────────────────

VixoKart.Wishlist = (function() {
  const STORAGE_KEY = 'vixokart_wishlist';

  function getAll() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  }

  function save(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (err) {
      console.error('[VixoKart Wishlist] Could not save:', err);
    }
  }

  function has(id) {
    return getAll().some(item => String(item.id) === String(id));
  }

  function toggle(product) {
    const items = getAll();
    const idx = items.findIndex(item => String(item.id) === String(product.id));
    let added;
    if (idx > -1) {
      items.splice(idx, 1);
      added = false;
    } else {
      items.push(product);
      added = true;
    }
    save(items);
    updateCountBadge();
    return added;
  }

  function updateCountBadge() {
    const count = getAll().length;
    document.querySelectorAll('[data-wishlist-count]').forEach(badge => {
      badge.textContent = count;
      badge.classList.toggle('hidden', count === 0);
    });
  }

  function syncButtons() {
    document.querySelectorAll('[data-wishlist]').forEach(btn => {
      const id = btn.dataset.productId;
      if (id && has(id)) btn.classList.add('active');
    });
    updateCountBadge();
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist]');
    if (!btn) return;

    const product = {
      id: btn.dataset.productId,
      title: btn.dataset.productTitle,
      url: btn.dataset.productUrl,
      image: btn.dataset.productImage,
      price: btn.dataset.productPrice,
    };

    const added = toggle(product);
    btn.classList.toggle('active', added);
    VixoKart.toast(added ? 'Added to wishlist' : 'Removed from wishlist');
  });

  document.addEventListener('DOMContentLoaded', syncButtons);
  if (document.readyState !== 'loading') syncButtons();

  return { getAll, has, toggle, updateCountBadge };
})();

// ─── Variant Selector (Product Page) ─────────────────────────────────────────────

VixoKart.VariantSelector = class {
  constructor(form) {
    this.form    = form;
    this.product = JSON.parse(form.dataset.product || '{}');
    this.options = form.querySelectorAll('[data-option]');
    this.selected = {};

    this.options.forEach(option => {
      this.selected[option.dataset.option] = option.value || option.dataset.value;
    });

    this.bindEvents();
    this.updateVariant();
  }

  bindEvents() {
    this.form.querySelectorAll('.swatch, .size-pill').forEach(btn => {
      btn.addEventListener('click', () => {
        const group = btn.closest('[data-option-group]');
        if (!group) return;

        group.querySelectorAll('.swatch, .size-pill').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        group.querySelectorAll('.swatch, .size-pill').forEach(b => {
          b.setAttribute('aria-pressed', String(b === btn));
        });
        const selectedLabel = group.querySelector('[id^="option-selected-"]');
        if (selectedLabel) selectedLabel.textContent = btn.dataset.value || '';
        this.selected[group.dataset.optionGroup] = btn.dataset.value;
        this.updateVariant();
      });
    });

    this.form.querySelectorAll('select[data-option]').forEach(select => {
      select.addEventListener('change', () => {
        this.selected[select.dataset.option] = select.value;
        const group = select.closest('[data-option-group]');
        const selectedLabel = group?.querySelector('[id^="option-selected-"]');
        if (selectedLabel) selectedLabel.textContent = select.value;
        this.updateVariant();
      });
    });
  }

  getSelectedVariant() {
    return this.product.variants?.find(v =>
      v.options.every((opt, i) => opt === this.selected[`option${i + 1}`])
    );
  }

  updateVariant() {
    const variant = this.getSelectedVariant();
    const productRoot = this.form.closest('.main-product') || document;
    const priceEl = productRoot.querySelector('[data-variant-price]');
    const compareEl = productRoot.querySelector('[data-variant-compare]');
    const addBtn = this.form.querySelector('[data-add-to-cart]');
    const stickyBar = productRoot.querySelector('.product-sticky-cart') || document.querySelector('.product-sticky-cart');
    const stickyPrice = productRoot.querySelector('.product-sticky-cart__price') || document.querySelector('.product-sticky-cart__price');
    const stickyVariant = productRoot.querySelector('[data-sticky-variant]') || document.querySelector('[data-sticky-variant]');
    const stickyAdd = productRoot.querySelector('[data-sticky-add]') || document.querySelector('[data-sticky-add]');
    const stickyBuyVariant = document.querySelector('[data-sticky-buy-variant]');
    const stickyBuyQty = document.querySelector('[data-sticky-buy-qty]');
    const imgEl  = productRoot.querySelector('.product-gallery__main-img');

    if (!variant) return;

    // Quantity-rule sold out: the variant can technically be "available" while
    // still unbuyable if its minimum order quantity exceeds what's left in
    // stock (e.g. sold in multiples of 6, but only 3 remain). Mirrors Dawn's
    // quantity_rule_soldout check.
    const rule = variant.quantity_rule || { min: 1, max: null, increment: 1 };
    const checksInventory = variant.inventory_management === 'shopify' && variant.inventory_policy !== 'continue';
    const quantityRuleSoldOut = checksInventory && rule.min > variant.inventory_quantity;
    const buyable = variant.available && !quantityRuleSoldOut;

    // Sync the quantity selector to the new variant's quantity_rule
    const qtyInput = productRoot.querySelector('#product-qty');
    const qtyWrap  = qtyInput?.closest('quantity-input');
    if (qtyInput) {
      qtyInput.min = rule.min;
      qtyInput.dataset.min = rule.min;
      qtyInput.step = rule.increment || 1;
      if (rule.max) {
        qtyInput.max = rule.max;
        qtyInput.dataset.max = rule.max;
      } else {
        qtyInput.removeAttribute('max');
        delete qtyInput.dataset.max;
      }
      // Re-clamp the current value into the new bounds
      const current = parseInt(qtyInput.value, 10) || rule.min;
      qtyInput.value = Math.min(rule.max || Infinity, Math.max(rule.min, current));
      qtyWrap?.validateQtyRules?.();
    }

    // Price update
    if (priceEl) priceEl.textContent = VixoKart.formatMoney(variant.price);
    if (stickyPrice) stickyPrice.textContent = VixoKart.formatMoney(variant.price);
    if (stickyVariant) stickyVariant.textContent = variant.title || '';
    if (compareEl) {
      if (variant.compare_at_price > variant.price) {
        compareEl.textContent = VixoKart.formatMoney(variant.compare_at_price);
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }

    // Add to cart button
    if (addBtn) {
      if (!buyable) {
        addBtn.textContent = window.themeStrings?.sold_out || 'Sold Out';
        addBtn.disabled = true;
      } else {
        addBtn.textContent = window.themeStrings?.add_to_cart || 'Add to Cart';
        addBtn.disabled = false;
      }
    }
    if (stickyAdd) stickyAdd.disabled = !buyable;

    // Sticky Buy Now — mirrors the main form's variant/quantity so the
    // dynamic checkout button in the sticky bar always checks out the
    // variant currently shown on the page.
    if (stickyBuyVariant) {
      stickyBuyVariant.value = variant.id;
      // Disabled inputs are excluded from form submission — this is what
      // hides/disables the dynamic checkout button when sold out.
      stickyBuyVariant.disabled = !buyable;
    }
    if (stickyBuyQty && qtyInput) stickyBuyQty.value = qtyInput.value;

    // Back in stock notify form
    const notifyBlock = document.querySelector('.notify-stock');
    if (notifyBlock) {
      notifyBlock.classList.toggle('hidden', variant.available);
      const bodyInput = notifyBlock.querySelector('.notify-stock__body-input');
      if (bodyInput) {
        bodyInput.value = `Back-in-stock request for: ${this.product.title} (${variant.title}) — ${window.location.origin}${this.product.url || window.location.pathname}`;
      }
    }

    // Stock indicator
    const stockEl = document.querySelector('[data-stock-indicator]');
    if (stockEl && typeof variant.inventory_quantity === 'number' && variant.inventory_management) {
      const qty = variant.inventory_quantity;
      const textEl = stockEl.querySelector('[data-stock-indicator-text]');
      stockEl.classList.remove('stock-indicator--in', 'stock-indicator--low', 'stock-indicator--out');
      if (qty > 10) {
        stockEl.classList.add('stock-indicator--in');
        if (textEl) textEl.textContent = 'In Stock';
      } else if (qty > 0) {
        stockEl.classList.add('stock-indicator--low');
        if (textEl) textEl.textContent = `Only ${qty} left!`;
      } else {
        stockEl.classList.add('stock-indicator--out');
        if (textEl) textEl.textContent = 'Out of Stock';
      }
    }

    // Featured image
    if (imgEl && variant.featured_image?.src) {
      imgEl.removeAttribute('srcset');
      imgEl.removeAttribute('sizes');
      imgEl.src = variant.featured_image.src;
      imgEl.dataset.zoomSrc = variant.featured_image.src.replace(/([?&])width=\d+/i, '$1width=1800');
    }

    // Update URL
    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());

    // Update hidden input
    const variantInput = this.form.querySelector('input[name="id"]');
    if (variantInput) {
      variantInput.value = variant.id;
      // Disabled inputs are excluded from form submission — this also blocks
      // Buy Now (form | payment_button), which reads straight from this form.
      variantInput.disabled = !buyable;
    }
  }
};

// Init variant selectors
document.querySelectorAll('.product-form').forEach(form => {
  if (form.dataset.product) new VixoKart.VariantSelector(form);
});

// ─── Sticky Add to Cart ──────────────────────────────────────────────────────────

(function initStickyCart() {
  const stickyBar = document.querySelector('.product-sticky-cart');
  const formArea  = document.querySelector('.product-buy');
  if (!stickyBar || !formArea) return;

  const observer = new IntersectionObserver(([entry]) => {
    stickyBar.classList.toggle('visible', !entry.isIntersecting);
  }, { threshold: 0 });

  observer.observe(formArea);
})();

// ─── Add to Cart (Product Page) ──────────────────────────────────────────────────

document.querySelectorAll('[data-type="add-to-cart-form"]').forEach(form => {
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = form.querySelector('[data-add-to-cart]');
    if (!btn || btn.disabled) return;

    btn.classList.add('btn--loading');

    try {
      const formData = new FormData(form);
      // The quantity input's min is now the variant's quantity_rule.min (never
      // 0), so this is just a defensive fallback rather than the primary guard.
      const rawQty = parseInt(formData.get('quantity'), 10);
      const body = JSON.stringify({
        id: formData.get('id'),
        quantity: rawQty > 0 ? rawQty : 1,
      });

      await VixoKart.fetchJSON(window.routes.cart_add_url + '.js', { method: 'POST', body });

      // Refresh cart drawer
      await VixoKart.Cart?.refresh();
      VixoKart.Cart?.open();
      VixoKart.toast('Added to cart!', 'success');

    } catch (err) {
      VixoKart.toast(err.message || 'Could not add to cart.', 'error');
    } finally {
      btn.classList.remove('btn--loading');
    }
  });
});

// ─── Quick Add to Cart ───────────────────────────────────────────────────────────

document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-quick-add]');
  if (!btn) return;

  // Product-card buttons sit inside the card link. Keep quick add in the cart
  // and prevent the surrounding link from navigating to the product page.
  e.preventDefault();
  e.stopPropagation();

  const variantId = btn.dataset.variantId;
  if (!variantId) return;

  btn.classList.add('product-card__plus-add--loading');
  btn.setAttribute('aria-busy', 'true');
  btn.disabled = true;

  try {
    await VixoKart.fetchJSON(window.routes.cart_add_url + '.js', {
      method: 'POST',
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    });
    await VixoKart.Cart?.refresh();
    VixoKart.Cart?.open();
    VixoKart.toast('Added to cart!', 'success');
  } catch (err) {
    VixoKart.toast('Could not add to cart.', 'error');
  } finally {
    btn.classList.remove('product-card__plus-add--loading');
    btn.setAttribute('aria-busy', 'false');
    btn.disabled = false;
  }
});

// ─── Quantity Input (Dawn-style web component) ───────────────────────────────────
// Product page only (cart drawer keeps its own debounced handler in cart-drawer.js,
// cart page keeps its own form-scoped handler in main-cart.liquid).
//
// Ported from Dawn's <quantity-input> custom element: uses the native
// stepUp()/stepDown() input methods so min/max/step (sourced from the variant's
// quantity_rule) are always respected by the browser itself, instead of doing
// manual min/max math by hand. Minimum is the variant's quantity_rule.min
// (normally 1) — quantity can never reach 0 here, so there's no "qty 0" edge
// case for Add to Cart or Buy Now to guard against.

// Keep the sticky Buy Now button's hidden quantity input in sync whenever
// the on-page quantity selector changes (typing, or the +/- buttons, which
// dispatch a native 'change' event — see QuantityInput below).
(function syncStickyBuyNowQuantity() {
  const qtyInput = document.querySelector('#product-qty');
  const stickyBuyQty = document.querySelector('[data-sticky-buy-qty]');
  if (!qtyInput || !stickyBuyQty) return;
  qtyInput.addEventListener('change', () => { stickyBuyQty.value = qtyInput.value; });
})();

if (!customElements.get('quantity-input')) {
  class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('.quantity-input');
      this.changeEvent = new Event('change', { bubbles: true });
      this.input?.addEventListener('change', () => this.validateQtyRules());
      this.querySelectorAll('.quantity-btn').forEach((button) =>
        button.addEventListener('click', this.onButtonClick.bind(this))
      );
    }

    connectedCallback() {
      this.validateQtyRules();
    }

    onButtonClick(event) {
      event.preventDefault();
      if (!this.input) return;
      const previousValue = this.input.value;

      if (event.currentTarget.name === 'plus') {
        this.input.stepUp();
      } else {
        this.input.stepDown();
      }

      if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
    }

    // Disables the minus/plus buttons once the input hits its min/max so users
    // get immediate feedback instead of relying on native input clamping alone.
    validateQtyRules() {
      if (!this.input) return;
      const value = parseInt(this.input.value, 10);

      if (this.input.min !== '') {
        const buttonMinus = this.querySelector('.quantity-btn[name="minus"]');
        if (buttonMinus) buttonMinus.disabled = value <= parseInt(this.input.min, 10);
      }
      if (this.input.max) {
        const buttonPlus = this.querySelector('.quantity-btn[name="plus"]');
        if (buttonPlus) buttonPlus.disabled = value >= parseInt(this.input.max, 10);
      }
    }
  }

  customElements.define('quantity-input', QuantityInput);
}

// ─── Collection — Grid/List Toggle ───────────────────────────────────────────────

(function initViewToggle() {
  const grid    = document.querySelector('.product-grid');
  const toggler = document.querySelector('.view-toggle');
  if (!grid || !toggler) return;

  toggler.querySelectorAll('.view-toggle__btn').forEach(btn => {
    btn.addEventListener('click', () => {
      toggler.querySelectorAll('.view-toggle__btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      const view = btn.dataset.view;
      grid.dataset.view = view;
    });
  });
})();

// ─── Load More (Collection) ──────────────────────────────────────────────────────

(function initLoadMore() {
  const btn = document.querySelector('[data-load-more]');
  if (!btn) return;

  btn.addEventListener('click', async () => {
    const nextUrl = btn.dataset.nextUrl;
    if (!nextUrl) return;

    btn.classList.add('btn--loading');
    btn.disabled = true;

    try {
      const response = await fetch(nextUrl);
      const text = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');

      const newItems = doc.querySelectorAll('.product-card');
      const grid = document.querySelector('.product-grid');
      newItems.forEach(item => {
        grid?.appendChild(item);
        VixoKart.observeReveal?.(item);
      });

      // Update next URL
      const newNextBtn = doc.querySelector('[data-load-more]');
      if (newNextBtn?.dataset.nextUrl) {
        btn.dataset.nextUrl = newNextBtn.dataset.nextUrl;
        btn.disabled = false;
        btn.classList.remove('btn--loading');
      } else {
        btn.remove();
      }
    } catch {
      btn.classList.remove('btn--loading');
      btn.disabled = false;
    }
  });
})();

// ─── Collection Filters — Price Slider ────────────────────────────────────────────

(function initPriceSlider() {
  const slider = document.querySelector('.price-range-slider');
  if (!slider) return;

  const minInput = slider.querySelector('#price-min');
  const maxInput = slider.querySelector('#price-max');
  const rangeMin = slider.querySelector('#range-min');
  const rangeMax = slider.querySelector('#range-max');

  if (!minInput || !maxInput || !rangeMin || !rangeMax) return;

  function updateSlider() {
    const min = parseFloat(rangeMin.value);
    const max = parseFloat(rangeMax.value);
    const total = parseFloat(rangeMax.max);
    rangeMin.style.setProperty('--min-pct', (min / total * 100) + '%');
    rangeMax.style.setProperty('--max-pct', (max / total * 100) + '%');
    minInput.value = min;
    maxInput.value = max;
  }

  rangeMin.addEventListener('input', () => {
    if (parseFloat(rangeMin.value) > parseFloat(rangeMax.value)) {
      rangeMin.value = rangeMax.value;
    }
    updateSlider();
  });

  rangeMax.addEventListener('input', () => {
    if (parseFloat(rangeMax.value) < parseFloat(rangeMin.value)) {
      rangeMax.value = rangeMin.value;
    }
    updateSlider();
  });

  updateSlider();
})();

// ─── Filter chips — dismiss ───────────────────────────────────────────────────────

document.addEventListener('click', (e) => {
  const chip = e.target.closest('.filter-chip');
  if (!chip) return;
  // Remove chip and redirect
  const url = new URL(window.location);
  const param = chip.dataset.param;
  const value = chip.dataset.value;
  if (param) {
    const existing = url.searchParams.getAll(param);
    url.searchParams.delete(param);
    existing.filter(v => v !== value).forEach(v => url.searchParams.append(param, v));
  }
  window.location = url.toString();
});

// ─── Social Share ─────────────────────────────────────────────────────────────────

document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-share]');
  if (!btn) return;

  const network = btn.dataset.share;
  const url = encodeURIComponent(window.location.href);
  const title = encodeURIComponent(document.title);

  const urls = {
    twitter:   `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
    facebook:  `https://www.facebook.com/sharer/sharer.php?u=${url}`,
    pinterest: `https://pinterest.com/pin/create/button/?url=${url}&description=${title}`,
    copy:      null,
  };

  if (network === 'copy') {
    navigator.clipboard.writeText(window.location.href).then(() => {
      VixoKart.toast('Link copied!', 'success');
    });
    return;
  }

  if (urls[network]) {
    window.open(urls[network], '_blank', 'width=600,height=400');
  }
});

// ─── Countdown Timer (Deals) ──────────────────────────────────────────────────────

document.querySelectorAll('[data-countdown]').forEach(el => {
  const target = new Date(el.dataset.countdown).getTime();

  function update() {
    const now  = Date.now();
    const diff = Math.max(0, target - now);

    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000)  / 60000);
    const s = Math.floor((diff % 60000)    / 1000);

    el.querySelector('[data-timer-hours]')  && (el.querySelector('[data-timer-hours]').textContent   = String(h + d * 24).padStart(2, '0'));
    el.querySelector('[data-timer-minutes]') && (el.querySelector('[data-timer-minutes]').textContent = String(m).padStart(2, '0'));
    el.querySelector('[data-timer-seconds]') && (el.querySelector('[data-timer-seconds]').textContent = String(s).padStart(2, '0'));

    if (diff > 0) requestAnimationFrame(() => setTimeout(update, 1000));
  }

  update();
});

// ─── Hero Slider Init (delegated to hero-slider.js) ─────────────────────────────────

// ─── Smooth scrolling for anchor links ───────────────────────────────────────────

document.addEventListener('click', (e) => {
  const link = e.target.closest('a[href^="#"]');
  if (!link) return;
  const id = link.getAttribute('href').slice(1);
  const target = document.getElementById(id);
  if (target) {
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
});

